import { useContext, useEffect, useState } from 'react'
import { CountriesProductDto } from '@/api/Clients/Types/CountriesProduct'
import { UpdateCountryProduct } from '@/api/Clients/Types/UpdateCountryProduct'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { fetchTickmillProductsAction } from '@/store/clients/clients.actions'
import { hasPermission } from '@/utils/permissionsValidator'
import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query'
import { Page } from '@progress/kendo-react-dropdowns'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { FilterCell } from 'components/Filter/FilterCell'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import copy from 'copy-to-clipboard'
import { normalizeFilter } from 'features/DataGrid'
import { useAppDispatch, useAppSelector } from 'store'
import { pageableSettings } from 'store/grid/grid.slice'
import { fetchCountriesAction, fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { IdentityApiClient } from 'utils/api/identityApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import stringComparator from 'utils/stringComparator'

import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import {
  AdministrationCountriesNavigation,
  AdministrationCountriesTab,
} from './AdministrationCountriesNavigation'
import countriesProducts from './countriesProducts'
import { EditCountryProductForm } from './EditCountryProductForm'

const AdministrationCountriesProducts = (): JSX.Element => {
  const identityApiClient = useApiClient(IdentityApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)

  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })
  const [paginationState, setPaginationState] = useState<Page>(getPaginationInitialState())
  const [product, setProduct] = useState<CountriesProductDto | null>(null)
  const [auditLogModal, setAuditLogModal] = useState<{
    visible: boolean
    data?: CountriesProductDto
  }>({
    visible: false,
  })

  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)
  const countries = useAppSelector((state) => state.identity.countries)
  const tickmillProducts = useAppSelector((state) => state.clients.tickmillProducts)

  const setToast = useContext(ToastContext)
  const dispatch = useAppDispatch()

  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh((query?: PageQuery) => {
    return clientsApiClient.getCountriesProducts({
      ...query,
    })
  }, [])
  const { data, isLoading } = useFetchOne(fetchData)

  useEffect(() => {
    if (isEmpty(tickmillCompanies)) {
      dispatch(fetchTickmillCompaniesAction(identityApiClient))
    }
  }, [dispatch, identityApiClient, tickmillCompanies])

  useEffect(() => {
    if (isEmpty(tickmillProducts)) {
      dispatch(fetchTickmillProductsAction(clientsApiClient))
    }
  }, [clientsApiClient, dispatch, tickmillProducts])

  useEffect(() => {
    if (isEmpty(countries)) {
      dispatch(fetchCountriesAction(identityApiClient))
    }
  }, [countries, dispatch, identityApiClient])

  const handleSubmit = async (companyCountryId: string, values: UpdateCountryProduct) => {
    await clientsApiClient.updateCountyProduct(companyCountryId, values)
    setToast(successToast('Company country updated.'))
    setProduct(null)
    forceRefresh()
  }

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(normalizeFilter(event.filter))
    setPaginationState({
      ...getPaginationInitialState(),
      skip: 0,
      take: paginationState.take,
    })
  }

  const handlePageChange = (event: GridPageChangeEvent) => {
    if (data) {
      setPaginationState({ skip: event.page.skip, take: event.page.take })
    }
  }

  const handleEdit = (prod: CountriesProductDto) => {
    setProduct(prod)
  }

  const setAuditLog = (record: CountriesProductDto) => {
    setAuditLogModal({ data: record, visible: true })
  }

  if (isLoading) {
    return <Spinner />
  }

  const gridData = filterBy(data || [], filter).sort(
    (a: CountriesProductDto, b: CountriesProductDto) =>
      stringComparator(a.tickmillCompany.name, b.tickmillCompany.name)
  )

  return (
    <>
      {product && (
        <EditCountryProductForm
          isOpen={!!product}
          onClose={() => setProduct(null)}
          data={product}
          onSubmit={handleSubmit}
        />
      )}
      {auditLogModal.visible && auditLogModal.data && (
        <ClientAreaAuditLogModal
          isVisible={auditLogModal.visible}
          onClose={() => setAuditLogModal({ visible: false, data: undefined })}
          countryId={auditLogModal.data.country.id}
        />
      )}

      <Breadcrumbs crumbs={[{ title: 'Products', link: links.administrationProducts.url }]} />
      <AdministrationCountriesNavigation tab={AdministrationCountriesTab.PRODUCTS} />
      <Grid
        filterable
        data={gridData.slice(paginationState.skip, paginationState.take + paginationState.skip)}
        filter={filter}
        skip={paginationState.skip}
        take={paginationState.take}
        total={gridData.length}
        onPageChange={handlePageChange}
        onFilterChange={handleFilterChange}
        sortable={{
          allowUnsort: true,
          mode: 'multiple',
        }}
        pageable={pageableSettings}
        style={{ zIndex: 0 }}
      >
        {countriesProducts
          .filter((c) => c.show)
          .map((column, idx) => (
            <Column key={idx} {...column} />
          ))}
        <Column
          title='Get Link'
          filterCell={() => null}
          sortable={false}
          cell={(props) => {
            if (!props.dataItem.forcedLink) {
              return <td />
            }
            return (
              <td
                align='center'
                style={{
                  color: 'var(--link)',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  copy(props.dataItem.forcedLink)
                  setToast(successToast('Link copied to clipboard'))
                }}
              >
                Get Link
              </td>
            )
          }}
        />
        {hasPermission(permissions, PermissionsEnum.CanEditCountryConfiguration) && (
          <Column
            width={50}
            title=''
            filterCell={FilterCell}
            sortable={false}
            cell={(props: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Edit',
                      'data-test': 'edit',
                      onClick: () => handleEdit(props.dataItem),
                    },
                    {
                      name: 'Audit Log',
                      'data-test': 'audit-log',
                      onClick: () => setAuditLog(props.dataItem),
                    },
                  ]}
                  data-test='more-button'
                />
              </td>
            )}
          />
        )}
      </Grid>
    </>
  )
}

export default AdministrationCountriesProducts
